import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import create from 'zustand'
import Modal from '@/components/Modal/Modal'
import Close from '@/components/ui/button/Close'
import Toolbar from '@/components/Toolbar/Toolbar'
import { SelectField } from '@/ui/components/Field/Select'
import { FormControls } from '@/components/ui/form/FormControls'
import { PrimaryButton } from '@/components/ui/button/PrimaryButton'
import { DefaultButton } from '@/components/ui/button/DefaultButton'
import { Field, Formik } from 'formik'
import { bookingTypes } from '@/utils/constants/booking.constants'
import { useMetadata } from '@/api/hooks/useMetadata'
import { useProjectStore } from '@/stores/projectStore'
import { SingleDatePickerField } from '@/ui/components/Field/SingleDatePickerField'
import { ShowValid } from '@/components/shared/ShowValid'
import { useUserCardFields } from '@/api/hooks/useUserCardFields'
import ReportData from './ReportData'
import { isAfter } from 'date-fns'
import EmployeesReportData from './EmployeesReportData'
import ObjectsReportData from './ObjectsReportData'
import { useIntl } from 'react-intl'
import { translate } from '@/i18n'
import ChevronDown from '@/images/Chevron_down_little.svg'; 

type CreateReportDialogStore = {
  isOpen: boolean
  onClose: () => void
}

export const useCreateReportDialogStore = create<CreateReportDialogStore>(
  (set) => ({
    id: undefined,
    isOpen: false,
    onClose: () => {
      set({
        isOpen: false
      })
    }
  })
)

export const createReportDialog = () => {
  useCreateReportDialogStore.setState({
    isOpen: true
  })
}

export const reportTypes = [
  { value: 'bookings', label: 'Бронирование' },
  { value: 'employees', label: 'Сотрудники' },
  { value: 'objects', label: 'Объекты' }
]

const headerTitle = {
  bookings: 'Отчет по бронированию',
  employees: 'Отчет по сотрудникам',
  objects: 'Отчет по объектам'
}

const CreateReportModal = () => {
  const isOpen = useCreateReportDialogStore((state) => state.isOpen)
  const onClose = useCreateReportDialogStore((state) => state.onClose)
  const nodes = useProjectStore((state) => state.nodes)
  const intl = useIntl()

  const [object, setObject] = useState('')
  const [reportSettings, setReportSettings] = useState<any>(null)

  useEffect(() => {
    if (isOpen) {
      setObject('')
      setReportSettings(null)
    }
  }, [isOpen])

  const { metadata } = useMetadata()
  const { data: userFields } = useUserCardFields()

  const layers =
    useMemo(
      () => nodes.map((node) => ({ value: String(node.id), label: node.name })),
      [nodes]
    ) || []
  const objectTypes = useProjectStore((state) => state.objectTypes)
  const userProps =
    useMemo(
      () =>
        userFields?.fields
          .filter((v) => v.alias != 'booking_category')
          .map((field) => ({
            label: field.label,
            key: field.uid
          })),
      [userFields]
    ) || []
  const nodeProps =
    useMemo(() => getNodeProps(metadata, object), [object]) || []

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth={625}>
      <Header>
        <Title>
          {reportSettings == null
            ? translate('add-report')
            : translate('create-report-' + reportSettings.type)}
        </Title>
        <Close color="#000" onClick={onClose} />
      </Header>

      <ShowValid valid={reportSettings == null}>
        <Formik
          initialValues={{
            type: 'bookings',
            bookingType: '',
            startDate: new Date(),
            endDate: new Date(),
            objectType: '',
            layer: '',
            cols: []
          }}
          onSubmit={(values) => {
            if (!values.cols.length) {
              alert(intl.formatMessage({ id: 'select-data-to-display' }))
              return
            }

            setReportSettings(values)
          }}
        >
          {({ handleSubmit, values, setFieldValue }) => (
            <form onSubmit={handleSubmit}>
              <Container>
                <Toolbar>
                  <Toolbar.Item xs={12} md={12}>
                    <Toolbar.Label style={{color: '#0A0A0A', fontSize: '14px', fontWeight: '500', marginBottom: '5px'}}>
                      {translate('select-report')}
                    </Toolbar.Label>
                    <SelectWrapper>
                      <Field
                        name="type"
                        component={SelectField}
                        $fullWidth
                        style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                        required={true}
                        options={reportTypes.map((item) => ({
                          ...item,
                          label: intl.formatMessage({ id: item.value })
                        }))}
                        onChange={(event) => {
                          const value = event.target.value
                          setFieldValue('type', value)
                          setFieldValue('cols', [])
                        }}
                      />
                    </SelectWrapper>

                  </Toolbar.Item>
                </Toolbar>

                <Toolbar>
                  <ShowValid valid={values.type === 'bookings'}>
                    <>
                      <Toolbar.Item xs={6} md={6}>
                        <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>
                          {translate('start-date')}
                        </Toolbar.Label>
                        <Field
                          name="startDate"
                          component={SingleDatePickerField}
                          $fullWidth
                          required={true}
                          style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                          placeholder={intl.formatMessage({ id: 'start-date' })}
                          onChange={(date) => {
                            if (isAfter(date, values.endDate)) {
                              setFieldValue('endDate', date)
                            }
                          }}
                        />
                      </Toolbar.Item>
                      <Toolbar.Item xs={6} md={6}>
                        <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>
                          {translate('end-date')}
                        </Toolbar.Label>
                        <Field
                          name="endDate"
                          component={SingleDatePickerField}
                          $fullWidth
                          required={true}
                          style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                          placeholder={intl.formatMessage({ id: 'end-date' })}
                          onChange={(date) => {
                            if (!isAfter(date, values.startDate)) {
                              setFieldValue('endDate', values.startDate)
                            }
                          }}
                        />
                      </Toolbar.Item>
                      <Toolbar.Item xs={12} md={12}>
                        <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>
                          {translate('booking-type')}
                        </Toolbar.Label>
                        <SelectWrapper>
                          <Field
                            name="bookingType"
                            component={SelectField}
                            $fullWidth
                            style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                            placeholder={intl.formatMessage({
                              id: 'booking-type'
                            })}
                            options={[
                              {
                                value: '',
                                label: intl.formatMessage({ id: 'all' })
                              }
                            ].concat(
                              bookingTypes.map((item) => ({
                                ...item,
                                label: intl.formatMessage({ id: item.label })
                              }))
                            )}
                          />
                        </SelectWrapper>
                      </Toolbar.Item>
                    </>
                  </ShowValid>
                  <ShowValid
                    valid={
                      values.type === 'bookings' || values.type === 'places'
                    }
                  >
                    <>
                      <Toolbar.Item xs={6} md={6}>
                        <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>
                          {translate('location')}
                        </Toolbar.Label>
                        <SelectWrapper>
                          <Field
                            name="layer"
                            component={SelectField}
                            $fullWidth
                            style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                            placeholder={intl.formatMessage({ id: 'location' })}
                            options={[
                              {
                                value: '',
                                label: intl.formatMessage({ id: 'all' })
                              }
                            ].concat(layers)}
                          />
                        </SelectWrapper>
                      </Toolbar.Item>
                      <Toolbar.Item xs={6} md={6}>
                        <Toolbar.Label style={{color: '#6C7384', fontSize: '12px'}}>
                          {translate('object-type')}
                        </Toolbar.Label>
                        <SelectWrapper>
                        <Field
                            name="objectType"
                            component={SelectField}
                            $fullWidth
                            placeholder={intl.formatMessage({
                              id: 'object-type'
                            })}
                            style={{ border: 'none', background: '#F2F3F4', height: '44px' }}
                            options={[
                              {
                                value: '',
                                label: intl.formatMessage({ id: 'all' })
                              }
                            ].concat(objectTypes)}
                            onChange={(event) => {
                              const value = event.target.value

                              setFieldValue('objectType', value)
                              setObject(value)

                              // if (values.objectType != value) {
                              //     setFieldValue('cols', [])
                              // }
                            }}
                          />
                        </SelectWrapper>
                      </Toolbar.Item>
                    </>
                  </ShowValid>
                </Toolbar>

                <Toolbar>
                  <Toolbar.Item xs={12} md={12}>
                    <Toolbar.Label style={{color: '#0A0A0A', fontSize: '14px', fontWeight: '500', marginBottom: '5px'}}>
                      {translate('select-data-to-display')}
                    </Toolbar.Label>
                    <CheckboxContainer>
                      <ShowValid valid={values.type === 'bookings'}>
                        <>
                          <label>
                            <Field type="checkbox" name="cols" value="user"/>
                            {translate('user')}
                          </label>
                          <label>
                            <Field type="checkbox" name="cols" value="name" />
                            {translate('place-name')}
                          </label>
                          <label>
                            <Field
                              type="checkbox"
                              name="cols"
                              value="objectType"
                            />
                            {translate('object-type')}
                          </label>
                          <label>
                            <Field type="checkbox" name="cols" value="layer" />
                            {translate('location')}
                          </label>
                          <label>
                            <Field
                              type="checkbox"
                              name="cols"
                              value="startDate"
                            />
                            {translate('start-date')}
                          </label>
                          <label>
                            <Field
                              type="checkbox"
                              name="cols"
                              value="endDate"
                            />
                            {translate('end-date')}
                          </label>
                          <label>
                            <Field
                              type="checkbox"
                              name="cols"
                              value="bookingType"
                            />
                            {translate('booking-type')}
                          </label>
                        </>
                      </ShowValid>

                      <ShowValid valid={values.type === 'employees'}>
                        <>
                          <label>
                            <Field
                              type="checkbox"
                              name="cols"
                              value="display"
                            />
                            {translate('user')}
                          </label>
                          {userProps.map((field) => (
                            <label>
                              <Field
                                type="checkbox"
                                name="cols"
                                value={field.key}
                              />
                              {field.label}
                            </label>
                          ))}
                        </>
                      </ShowValid>

                      <ShowValid valid={values.type === 'objects'}>
                        <>
                          <label>
                            <Field type="checkbox" name="cols" value="name" />
                            {translate('name')}
                          </label>
                          <label>
                            <Field
                              type="checkbox"
                              name="cols"
                              value="objectType"
                            />
                            {translate('object-type')}
                          </label>
                          <label>
                            <Field type="checkbox" name="cols" value="layer" />
                            {translate('location')}
                          </label>
                          {/* {nodeProps.map(field => (
                                                        <label>
                                                            <Field type="checkbox" name="cols" value={'props-' + field.value} />{field.label}
                                                        </label>
                                                    ))} */}
                        </>
                      </ShowValid>
                    </CheckboxContainer>
                  </Toolbar.Item>
                </Toolbar>

                <ControlsWrapper>
                  <FormControls>
                    <PrimaryButton type="submit" style={{color: '#fff', background: '#C12423', borderRadius: '8px'}}>
                      {translate('create-report')}
                    </PrimaryButton>
                    <DefaultButton 
                      type="button"
                      onClick={onClose}
                      style={{
                        color: '#C12423',
                        background: '#fff',
                        borderRadius: '8px',
                        border: '1px solid #C12423'
                      }}>
                      {translate('cancel')}
                    </DefaultButton>
                  </FormControls>
                </ControlsWrapper>
              </Container>
            </form>
          )}
        </Formik>
      </ShowValid>

      <ShowValid
        valid={reportSettings !== null && reportSettings.type === 'bookings'}
      >
        <ReportData
          settings={reportSettings}
          onClose={onClose}
          layers={layers}
          objectTypes={objectTypes}
          nodeProps={nodeProps}
        />
      </ShowValid>

      <ShowValid
        valid={reportSettings !== null && reportSettings.type === 'employees'}
      >
        <EmployeesReportData
          settings={reportSettings}
          onClose={onClose}
          userProps={userProps}
        />
      </ShowValid>

      <ShowValid
        valid={reportSettings !== null && reportSettings.type === 'objects'}
      >
        <ObjectsReportData
          settings={reportSettings}
          onClose={onClose}
          layers={layers}
          objectTypes={objectTypes}
          nodeProps={nodeProps}
        />
      </ShowValid>
    </Modal>
  )
}

export default CreateReportModal

const getNodeProps = (meta, uid) => {
  if (!meta || !meta.rawNodes || !meta.rawNodes[uid]) {
    return []
  }
  const node = meta.rawNodes[uid]
  const pluginData = node.plugin_data
  const keys = Object.keys(pluginData)

  const data = keys
    .map((key) => node.plugin_data[key])
    .filter((v) => v)
    .find((item) => item.fields)?.fields

  if (!data) return []

  return data.map((field) => ({ value: field.id, label: field.name }))
}

const CheckboxContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 32px;
  padding-top: 8px;
  color: #2F323C;
  font-size: 16px;
  font-weight: 400;

  label {
    display: flex;
    align-items: center;

    input {
      width: 20px;
      height: 20px;
      margin-right: 5px;
      accent-color: #373537;
    }
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ControlsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Container = styled.div``

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`

const Title = styled.div`
  font-weight: 500;
  font-size: 20px;
  line-height: 24px;
  color: #0A0A0A;
`

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    background-image: url(${ChevronDown});
    background-size: contain;
    background-repeat: no-repeat;
    pointer-events: none;
  }
`;